import { useEffect, useMemo, useCallback } from 'react';
import { useRouteMatch } from 'react-router';
import { buildAgentsPath } from '../../app/Routes/orgRouteUtils';

import {
  Tabs,
  Tab,
  WidgetSettingsBuilder,
  Breadcrumbs,
  Loading,
} from '../../components';
import { AGENT_TAB_PERSISTENCE_CONFIG, AGENT_OUTER_TABS } from './tabs';
import TelephonyTab from './TelephonyTab';
import { useAgent } from './hooks/useAgent';
import WebLinkGeneratorTab from './WebLinkGeneratorTab';
import { useAgentWidgetSettings } from './hooks/useWidgetSettings';
import { AgentTabs } from './AgentFormTab';
import { useTabPersistence } from '../../shared';
import type { TabsEnum } from './tabs';
import type { FormJsonData } from '../../api';

const AgentPageTabs = () => {
  const { params } = useRouteMatch<{
    agent_id: string;
    organization_id: string;
  }>();
  const {
    loading,
    agent,
    form,
    onSubmit,
    onChange,
    knowledgeData,
    activeTab: agentActiveTab,
    setActiveAgentTab,
    ...rest
  } = useAgent(params?.agent_id);
  const {
    handleUpdateWidgetSettings,
    data,
    getWidgetSettings,
    setWidgetData,
    isLoading = true,
    importing,
    schema,
    handleUploadMedia,
    importWidgetSettingsFromAgent,
  } = useAgentWidgetSettings();

  const { selectedTab, setSelectedTab: setActiveTab } = useTabPersistence(
    AGENT_TAB_PERSISTENCE_CONFIG,
    AGENT_OUTER_TABS
  );
  const activeTab = (selectedTab ?? 'agent') as TabsEnum;

  const handleSaveWidgetSettings = () => {
    handleUpdateWidgetSettings(params?.agent_id, {
      widgetSettings: { ...data },
    });
  };

  const handleUploadAgentMedia = async (
    file: Blob
  ): Promise<string | undefined> => {
    const response = await handleUploadMedia(params?.agent_id, file);

    return response?.cdnUrl;
  };

  const handleWidgetSettingsChange = useCallback(
    (settings: Record<string, unknown>) => {
      setWidgetData(settings);
    },
    [setWidgetData]
  );

  const widgetSettingsOptions = useMemo(() => {
    const allOptions = rest?.agentsOptions || [];
    const currentAgentId = params?.agent_id;

    if (!currentAgentId) {
      return allOptions;
    }

    return allOptions
      .filter(
        (option: { value: string; label: string }) =>
          option.value !== currentAgentId
      )
      .sort((a: { label: string }, b: { label: string }) =>
        a.label.localeCompare(b.label)
      );
  }, [rest?.agentsOptions, params?.agent_id]);

  const handleApplyFromAgent = useCallback(
    async (sourceAgentId: string) => {
      await importWidgetSettingsFromAgent(sourceAgentId);
    },
    [importWidgetSettingsFromAgent]
  );

  const widgetSettingData = useMemo(() => data || {}, [data]);

  useEffect(() => {
    if (activeTab === 'widgetFormSettings') {
      getWidgetSettings(params?.agent_id);
    }
  }, [activeTab, getWidgetSettings, params?.agent_id]);

  return (
    <div>
      <div className="flex gap-md flex-wrap justify-between items-center mb-md">
        <Breadcrumbs
          className="text-small"
          subtitle="Agents"
          subtitleUrl={buildAgentsPath(params?.organization_id ?? '')}
          title={agent?.title ?? 'Title'}
        />
      </div>
      <Tabs<TabsEnum>
        tabs={[
          { name: 'agent', label: 'Agent' },
          { name: 'widgetFormSettings', label: 'Widget Settings' },
          { name: 'webLinkGenerator', label: 'Web Link Generator' },
          { name: 'telephony', label: 'Inbound SIP Trunk' },
        ]}
        contentClassName="mt-md"
        activeTab={activeTab}
        onChange={setActiveTab}
      >
        <Tab name="agent">
          <>
            {loading ? (
              <Loading />
            ) : (
              <AgentTabs
                {...rest}
                activeTab={agentActiveTab}
                knowledgeData={knowledgeData || []}
                onSubmit={onSubmit}
                onChange={onChange}
                setActiveTab={setActiveAgentTab}
                agent={agent}
                form={form!}
                loading={loading}
              />
            )}
          </>
        </Tab>
        <Tab name="widgetFormSettings">
          {isLoading ? (
            <Loading />
          ) : (
            <WidgetSettingsBuilder
              onChange={handleWidgetSettingsChange}
              widgetSettings={widgetSettingData}
              schemaData={schema as FormJsonData}
              intializing={isLoading}
              onSave={handleSaveWidgetSettings}
              showSaveButton
              handleFileUpload={handleUploadAgentMedia}
              options={widgetSettingsOptions}
              sourceAgentOptions={widgetSettingsOptions}
              currentAgentName={agent?.title ?? 'Current agent'}
              onApplyFromAgent={handleApplyFromAgent}
              applyFromAgentLoading={importing}
            />
          )}
        </Tab>
        <Tab name="webLinkGenerator">
          <WebLinkGeneratorTab agentId={params?.agent_id} />
        </Tab>
        <Tab name="telephony">
          <TelephonyTab agentId={params?.agent_id} />
        </Tab>
      </Tabs>
    </div>
  );
};

export default AgentPageTabs;
